import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { ChatSummary } from '../api/client.ts';
import { useChat } from '../chat/ChatContext.tsx';
import { ChatList } from './ChatList.tsx';
import '../screens/SettingsScreen.css';
import './ChatPicker.css';

/**
 * The list of chats a message can go to — one screen, two ways in.
 *
 * «Переслать» opens it over a conversation and «Отправить в чат» opens it for
 * something shared into the app from outside, and the canvas draws one list for
 * both: whatever is being sent, the question is the same and so is the answer.
 * What differs is the heading and the line at the bottom, which is why those are
 * props and everything else is not.
 *
 * The rows are the chat list's own — `filtered` is what makes it draw a flat
 * list with «БОТ» beside the bots instead of the divider and the folders a whole
 * list is read through. That is exactly the shape the handoff asks for here, and
 * it also settles the note in the README's «Deviations» that said the tag was
 * wanted «в пересылке и в выборе получателя» — screens that did not exist.
 *
 * There is nothing to filter out: `chats` is what the server already decided
 * this person may see, hidden bots and all (hiddenBotsFor in
 * server/src/bot-access.ts), and every chat in it is one they may write to.
 * Including the one they are standing in — forwarding a message back into its
 * own conversation is a strange thing to want and not a thing to refuse.
 */
export function ChatPicker({
  title,
  hint,
  busy = false,
  error = null,
  onPick,
  onClose,
}: {
  title: string;
  /** The line under the list. The canvas gives one to «Переслать» only. */
  hint?: string;
  /** A send is in flight: the rows stop answering rather than starting a second. */
  busy?: boolean;
  error?: string | null;
  onPick: (chat: ChatSummary) => void;
  onClose: () => void;
}) {
  const { chats, chatsLoading } = useChat();
  const [query, setQuery] = useState('');

  // The same two lines that narrow the sidebar, on the same field: a picker is
  // the chat list with one question attached, and it should answer to a typed
  // name the way the list already does.
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? chats.filter((chat) => chat.title.toLowerCase().includes(needle)) : chats;
  }, [chats, query]);

  return (
    <div className="app-frame settings-screen chat-picker">
      <header className="settings-header settings-header-divided">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <X size={20} />
        </button>
        <h2 className="settings-heading chat-picker-heading">{title}</h2>
      </header>

      <div className="chat-picker-search">
        <input
          className="text-field"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Поиск чата"
          aria-label="Поиск чата"
        />
      </div>

      <div className={`chat-picker-list${busy ? ' chat-picker-busy' : ''}`}>
        {shown.length === 0 && !chatsLoading ? (
          // The canvas's own words, and narrower than the list's «Ничего не
          // найдено»: here there is only one thing to have failed to find.
          <div className="chat-picker-empty">Чат не найден</div>
        ) : (
          <ChatList
            chats={shown}
            activeChatId={null}
            loading={chatsLoading}
            // Always: this list is flat and tagged whether or not a word has
            // narrowed it. See the note above.
            filtered
            typingChatIds={[]}
            onSelect={(chat) => {
              if (!busy) onPick(chat);
            }}
            // A hold means nothing here. The rows carry the gesture because
            // every chat row does; there is simply nothing behind it.
            onHold={() => {}}
          />
        )}
      </div>

      {/* One line, and the error takes the slot the hint was in: they are the
          same sentence at two moments — what tapping a row will do, and what it
          did instead. */}
      {(error ?? hint) && (
        <div className={`chat-picker-foot${error ? ' chat-picker-foot-error' : ''}`}>
          {error ?? hint}
        </div>
      )}
    </div>
  );
}
